import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useEvents from "../../api/events";
import { useTheme } from "../../context/ThemeContext";
import {
  Calendar,
  Plus,
  Users,
  Pencil,
  Trash2,
  MapPin,
  Search,
  Image as ImageIcon,
  Clock,
  Ticket,
  GraduationCap,
  CreditCard,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { parseUTCDate } from "../../lib/utils";

// Small badge showing whether a row is an "Evenement" or an "Inschrijving".
const TypeBadge = ({ type, isDark }) => {
  const isEnrollment = type === "enrollment";
  const Icon = isEnrollment ? GraduationCap : Ticket;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${
        isEnrollment
          ? isDark
            ? "bg-blue-500/15 text-blue-300"
            : "bg-blue-50 text-blue-600"
          : isDark
            ? "bg-white/[0.06] text-gray-400"
            : "bg-gray-100 text-gray-500"
      }`}
    >
      <Icon className="w-2.5 h-2.5" />
      {isEnrollment ? "Inschrijving" : "Evenement"}
    </span>
  );
};

// De formules van een activiteit. De kolom is JSON, dus meestal al een array;
// oudere rijen kunnen nog een string bevatten.
const eventFormules = (event) => {
  let raw = event?.formules;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  return Array.isArray(raw) ? raw : [];
};

// Badge indicating that a paid enrollment requires online payment.
//
// Werkt de activiteit met formules, dan staan de bedragen dáár en niet op de
// activiteit zelf — één prijs tonen kan dan niet. In dat geval tonen we de
// spreiding over alle formules ("€90 – €150"), zodat de lijst nog altijd zegt
// waar het over gaat.
const PaymentBadge = ({ event, isDark }) => {
  if (!event.payment_required) return null;

  const formules = eventFormules(event);
  let label;
  let title;

  if (formules.length > 0) {
    const prijzen = formules
      .flatMap((f) => [f?.price_member, f?.price_non_member])
      .filter((v) => v != null && v !== "")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    const min = Math.min(...prijzen);
    const max = Math.max(...prijzen);
    label =
      prijzen.length === 0
        ? "Variabele prijs"
        : min === max
          ? `€${min}`
          : `€${min} – €${max}`;
    title = `Prijs verschilt per formule (${formules.length} ${
      formules.length === 1 ? "formule" : "formules"
    })`;
  } else {
    const member =
      event.price_member != null ? `€${Number(event.price_member)}` : null;
    const nonMember =
      event.price_non_member != null
        ? `€${Number(event.price_non_member)}`
        : null;
    label =
      member && nonMember
        ? `${member} / ${nonMember}`
        : member || nonMember || "Betaald";
    title = "Ledenprijs / niet-ledenprijs";
  }

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md ${
        isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600"
      }`}
    >
      <CreditCard className="w-2.5 h-2.5" />
      {label}
    </span>
  );
};

const EventsManager = () => {
  const navigate = useNavigate();
  const { getAllEvents, deleteEvent } = useEvents();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEvent(deleteTarget.id);
      setDeleteTarget(null);
      fetchEvents();
    } catch (e) {
      console.error("Failed to delete", e);
    } finally {
      setDeleting(false);
    }
  };

  const filteredEvents = events.filter(
    (e) =>
      (e.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.location || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1
            className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Inschrijvingen
          </h1>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Beheer je evenementen, lessen, betalingen en inschrijvingen
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/events/new")}
          className={`h-9 px-4 rounded-lg text-[13px] font-medium flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
        >
          <Plus className="w-3.5 h-3.5" />
          Nieuw Evenement
        </button>
      </div>

      {/* Search & Stats */}
      <div
        className={`rounded-xl p-4 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="w-full md:flex-1 max-w-md relative">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder="Zoek op naam of locatie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
            />
          </div>
          <div
            className={`flex items-center gap-1.5 text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            <Calendar className="w-3.5 h-3.5" />
            {events.length} evenement{events.length !== 1 ? "en" : ""}
          </div>
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div
          className={`rounded-xl border p-12 ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <div className="flex items-center justify-center">
            <div
              className={`animate-spin rounded-full h-6 w-6 border-2 ${isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-900"}`}
            ></div>
          </div>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div
          className={`rounded-xl border p-16 text-center ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <div
            className={`h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
          >
            <Calendar
              className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-300"}`}
            />
          </div>
          <h3
            className={`text-[13px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
          >
            {searchTerm ? "Geen resultaten" : "Geen evenementen"}
          </h3>
          <p
            className={`text-[12px] mb-4 max-w-xs mx-auto ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            {searchTerm
              ? "Geen evenementen gevonden voor je zoekopdracht."
              : "Maak je eerste evenement aan om te beginnen met inschrijvingen."}
          </p>
          {!searchTerm && (
            <button
              onClick={() => navigate("/dashboard/events/new")}
              className={`h-8 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <Plus className="w-3 h-3 inline mr-1.5" />
              Eerste evenement
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {filteredEvents.map((event, i) => {
              const eventDate = parseUTCDate(event.date);
              const isPast = eventDate && eventDate < new Date();
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                >
                  <div className="p-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-11 w-11 rounded-lg overflow-hidden border flex-shrink-0 flex items-center justify-center ${isDark ? "bg-white/[0.04] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
                      >
                        {event.imageUrl ? (
                          <img
                            src={event.imageUrl}
                            alt={event.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Calendar
                            className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {event.name}
                          </span>
                          {isPast && (
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md flex-shrink-0 ${isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-400"}`}
                            >
                              Afgelopen
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          <TypeBadge type={event.type} isDark={isDark} />
                          <PaymentBadge event={event} isDark={isDark} />
                        </div>
                        {eventDate && (
                          <span
                            className={`text-[11px] block mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {format(eventDate, "dd/MM/yyyy HH:mm", {
                              locale: nl,
                            })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/events/${event.id}/registrations`,
                            )
                          }
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 active:bg-white/[0.06]" : "text-gray-400 active:bg-gray-100"}`}
                        >
                          <Users className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/dashboard/events/${event.id}`)
                          }
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(event)}
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 active:bg-red-500/15" : "text-gray-400 active:bg-red-50"}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Desktop list */}
          <div
            className={`hidden sm:block rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
            >
              {filteredEvents.map((event, i) => {
                const eventDate = parseUTCDate(event.date);
                const isPast = eventDate && eventDate < new Date();

                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`p-4 transition-colors ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div
                        className={`h-11 w-11 rounded-lg overflow-hidden border flex-shrink-0 flex items-center justify-center ${isDark ? "bg-white/[0.04] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
                      >
                        {event.imageUrl ? (
                          <img
                            src={event.imageUrl}
                            alt={event.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <ImageIcon
                            className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <span
                            className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {event.name}
                          </span>
                          <TypeBadge type={event.type} isDark={isDark} />
                          <PaymentBadge event={event} isDark={isDark} />
                          {isPast && (
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-400"}`}
                            >
                              Afgelopen
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          {eventDate && (
                            <span
                              className={`flex items-center gap-1 text-[12px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              <Calendar className="w-3 h-3" />
                              {format(eventDate, "dd/MM/yyyy", { locale: nl })}
                            </span>
                          )}
                          {eventDate && (
                            <span
                              className={`flex items-center gap-1 text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            >
                              <Clock className="w-3 h-3" />
                              {format(eventDate, "HH:mm", { locale: nl })}
                            </span>
                          )}
                          {event.location && (
                            <span
                              className={`flex items-center gap-1 text-[12px] truncate max-w-[200px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                              title={event.location}
                            >
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              {event.location}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/events/${event.id}/registrations`,
                            )
                          }
                          className={`h-8 px-3 rounded-lg text-[12px] font-medium items-center gap-1.5 border transition-colors hidden md:flex ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04] hover:text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                        >
                          <Users className="w-3.5 h-3.5" />
                          Inschrijvingen
                        </button>
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/events/${event.id}/registrations`,
                            )
                          }
                          className={`h-7 w-7 rounded-lg flex items-center justify-center md:hidden transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/dashboard/events/${event.id}`)
                          }
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(event)}
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation (custom modal — never window.confirm) */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative w-full max-w-sm rounded-2xl border p-6 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
          >
            <div
              className={`h-10 w-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-red-500/10" : "bg-red-50"}`}
            >
              <AlertTriangle
                className={`h-5 w-5 ${isDark ? "text-red-400" : "text-red-500"}`}
              />
            </div>
            <h3
              className={`text-[14px] font-semibold text-center mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Verwijderen?
            </h3>
            <p
              className={`text-[12px] text-center mb-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Weet je zeker dat je{" "}
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                {deleteTarget.name}
              </span>{" "}
              wilt verwijderen? Alle bijbehorende inschrijvingen gaan verloren.
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className={`flex-1 h-9 rounded-lg text-[13px] font-medium border transition-colors disabled:opacity-50 ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 h-9 rounded-lg text-[13px] font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
